import React, { useMemo, useRef, useState , useEffect} from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const isNumericMirrorStream = (streamItem, allStreams = []) => {
  if (!streamItem?.hasVideo) {
    return false;
  }

  const streamId = String(streamItem?.id ?? "");
  if (!/^\d+$/.test(streamId)) {
    return false;
  }

  return allStreams.some((candidate) => {
    if (!candidate?.stream || !candidate?.hasVideo) {
      return false;
    }
    return String(candidate.id ?? "") === `play_${streamId}`;
  });
};

const hasActiveVideoTrack = (stream) => {
  if (!stream || typeof stream.getVideoTracks !== "function") {
    return false;
  }

  return stream.getVideoTracks().some((track) => track.readyState === "live");
};

const hasActiveAudioTrack = (stream) => {
  if (!stream || typeof stream.getAudioTracks !== "function") {
    return false;
  }

  return stream.getAudioTracks().some((track) => track.readyState === "live");
};

export function VideoStreamArea({ remoteStreams, callStreamId, darkMode }) {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const videoStreams = useMemo(
    () =>
      remoteStreams.filter((r) => {
        if (!r.stream || !r.hasVideo || r.id === callStreamId || r.isLocalEcho) {
          return false;
        }

        if (isNumericMirrorStream(r, remoteStreams)) {
          return false;
        }

        return true;
      }),
    [callStreamId, remoteStreams]
  );

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (typeof document === "undefined") {
        return;
      }
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement === containerRef.current) {
        const exitPromise = document.exitFullscreen?.();
        if (exitPromise && typeof exitPromise.catch === "function") {
          exitPromise.catch(() => {});
        }
      }
    };
  }, []);

  const toggleFullscreen = async () => {
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    try {
      if (document.fullscreenElement === containerEl) {
        await document.exitFullscreen?.();
        return;
      }

      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
      }

      await containerEl.requestFullscreen?.();
    } catch (err) {
      console.warn("[WaitingCallPanel] Fullscreen toggle failed", err);
    }
  };

  if (videoStreams.length === 0) {
    return null;
  }

  const totalStreams = videoStreams.length;

  return (
    <MDBox
      ref={containerRef}
      sx={{
        width: "100%",
        display: "flex",
        gap: isFullscreen ? 1.5 : 1,
        flexWrap: "wrap",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
        border: (theme) =>
          `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
        p: isFullscreen ? 1 : 0,
        minHeight: isFullscreen ? "100vh" : 220,
        maxHeight: isFullscreen ? "100vh" : "60%",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      >
        <Tooltip title={isFullscreen ? "Exit full screen" : "Full screen"}>
          <IconButton
            size="small"
            onClick={toggleFullscreen}
            sx={{
              width: 28,
              height: 28,
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.42),
              color: "common.white",
              border: (theme) =>
                `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.62),
              },
            }}
          >
            {isFullscreen ? (
              <CloseFullscreenOutlinedIcon sx={{ fontSize: 17 }} />
            ) : (
              <FullscreenOutlinedIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {videoStreams.map((r) => (
        <Box
          key={r.id}
          sx={{
            flex: isFullscreen ? "1 1 100%" : "1 1 45%",
            minWidth: isFullscreen ? "100%" : 120,
            minHeight: isFullscreen ? "100%" : 170,
            maxHeight: isFullscreen ? "100%" : 460,
            borderRadius: isFullscreen ? 0 : 1.5,
            overflow: "hidden",
            bgcolor: "black",
            position: "relative",
          }}
        >
          <video
            autoPlay
            playsInline
            muted
            ref={(el) => {
              if (el && r.stream && el.srcObject !== r.stream) {
                el.srcObject = r.stream;
                try {
                  el.play?.().catch((err) => {
                    console.warn(
                      `[StreamPlayer]Playing VIDEO stream failed | id=${r.id}`,
                      err,
                    );
                  });
                  console.log(
                    `[StreamPlayer] Playing VIDEO stream | id=${r.id} | totalActive=${totalStreams}`
                  );
                } catch {}
              }
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      ))}
    </MDBox>
  );
}

export function AudioStreamArea({ remoteStreams, videoPanelVisible }) {
  const [audioTrackVersion, setAudioTrackVersion] = useState(0);
  const audioElementsRef = useRef({});

  const getAudioKey = (streamItem, index) =>
    `${String(streamItem?.id ?? "audio")}-${String(
      streamItem?.stream?.id ?? "stream",
    )}-${index}`;

  useEffect(() => {
    const refreshAudioState = () => {
      setAudioTrackVersion((prev) => prev + 1);
    };

    const trackedStreams = [];
    const trackedTracks = [];

    remoteStreams.forEach((streamItem) => {
      if (!streamItem?.stream) {
        return;
      }

      const stream = streamItem.stream;
      trackedStreams.push(stream);
      stream.addEventListener?.("addtrack", refreshAudioState);
      stream.addEventListener?.("removetrack", refreshAudioState);

      const tracks = [
        ...(stream.getAudioTracks?.() || []),
        ...(stream.getVideoTracks?.() || []),
      ];

      tracks.forEach((track) => {
        trackedTracks.push(track);
        track.addEventListener?.("ended", refreshAudioState);
        track.addEventListener?.("mute", refreshAudioState);
        track.addEventListener?.("unmute", refreshAudioState);
      });
    });

    return () => {
      trackedStreams.forEach((stream) => {
        stream.removeEventListener?.("addtrack", refreshAudioState);
        stream.removeEventListener?.("removetrack", refreshAudioState);
      });

      trackedTracks.forEach((track) => {
        track.removeEventListener?.("ended", refreshAudioState);
        track.removeEventListener?.("mute", refreshAudioState);
        track.removeEventListener?.("unmute", refreshAudioState);
      });
    };
  }, [remoteStreams]);

  const audioStreams = useMemo(
    () =>
      remoteStreams.filter((streamItem) => {
        const stream = streamItem?.stream;
        if (!stream || streamItem?.isLocalEcho || !hasActiveAudioTrack(stream)) {
          return false;
        }

        if (!videoPanelVisible) {
          return true;
        }

        if (!streamItem?.hasVideo) {
          return true;
        }

        // Keep audio alive when remote stream advertises video but has no live video track.
        return !hasActiveVideoTrack(stream);
      }),
    [audioTrackVersion, remoteStreams, videoPanelVisible],
  );

  useEffect(() => {
    const activeKeys = new Set();

    audioStreams.forEach((streamItem, index) => {
      const key = getAudioKey(streamItem, index);
      activeKeys.add(key);

      const element = audioElementsRef.current[key];
      if (!element || !streamItem?.stream) {
        return;
      }

      if (element.srcObject !== streamItem.stream) {
        element.srcObject = streamItem.stream;
      }

      element.play?.().catch((err) => {
        console.warn(
          `[StreamPlayer] Audio stream play retry failed | id=${streamItem.id}`,
          err,
        );
      });
    });

    Object.keys(audioElementsRef.current).forEach((key) => {
      if (!activeKeys.has(key)) {
        delete audioElementsRef.current[key];
      }
    });
  }, [audioStreams, audioTrackVersion]);

  if (audioStreams.length === 0) {
    return null;
  }

  return (
    <>
      {audioStreams.map((r, index) => (
        <audio
          key={getAudioKey(r, index)}
          autoPlay
          playsInline
          ref={(el) => {
            const key = getAudioKey(r, index);

            if (el) {
              audioElementsRef.current[key] = el;
            } else {
              delete audioElementsRef.current[key];
            }

            if (el && r.stream && el.srcObject !== r.stream) {
              el.srcObject = r.stream;
              try {
                el.play?.().catch((err) => {
                  console.warn(
                    `[StreamPlayer] Audio stream play failed | id=${r.id}`,
                    err,
                  );
                });
                console.log(
                  `[StreamPlayer] Playing AUDIO stream | id=${r.id} | totalActive=${audioStreams.length}`,
                );
              } catch {}
            }
          }}
          style={{ display: "none" }}
        />
      ))}
    </>
  );
}

export default function WaitingCallPanel({
  callList = [],
  remoteStreams = [],
  callStreamId,
  showVideoPanel = true,
  darkMode,
  onJoin,
  onReject,
}) {
  const hasVideo =
    showVideoPanel &&
    remoteStreams.some((r) => {
      if (!r.stream || !r.hasVideo || r.id === callStreamId) {
        return false;
      }
      if (isNumericMirrorStream(r, remoteStreams)) {
        return false;
      }
      return hasActiveVideoTrack(r.stream);
    });

  return (
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        p: 0.75,
      }}
    >
      {hasVideo && (
        <VideoStreamArea
          remoteStreams={remoteStreams}
          callStreamId={callStreamId}
          darkMode={darkMode}
        />
      )}

      <AudioStreamArea
        remoteStreams={remoteStreams}
        videoPanelVisible={hasVideo}
      />

      {callList.length === 0 ? (
        <MDBox
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MDTypography variant="body2" color="text" sx={{ opacity: 0.6 }}>
            No waiting calls
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.65,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.2,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
              display: "none",
              background: "transparent",
            },
          }}
        >
          {callList.map((call) => (
            <Box
              key={call.roomId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 0.85,
                gap: 0.85,
                borderRadius: 1.4,
                background: darkMode
                  ? call.isSOS
                    ? "linear-gradient(135deg, rgba(255,82,82,0.15), rgba(255,23,68,0.08))"
                    : call.isAmbient || call.type === "Ambient"
                      ? "linear-gradient(135deg, rgba(255,183,77,0.16), rgba(255,152,0,0.08))"
                      : "linear-gradient(135deg, rgba(66,165,245,0.1), rgba(33,150,243,0.05))"
                  : call.isSOS
                    ? "linear-gradient(135deg, rgba(255,82,82,0.1), rgba(255,23,68,0.05))"
                    : call.isAmbient || call.type === "Ambient"
                      ? "linear-gradient(135deg, rgba(255,183,77,0.12), rgba(255,152,0,0.05))"
                      : "linear-gradient(135deg, rgba(66,165,245,0.08), rgba(33,150,243,0.03))",
                border: (theme) =>
                  `1.5px solid ${alpha(
                    call.isSOS
                      ? theme.palette.error.main
                      : call.isAmbient || call.type === "Ambient"
                        ? theme.palette.warning.main
                        : theme.palette.info.main,
                    0.3,
                  )}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: (theme) =>
                    `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.85,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: call.isSOS
                      ? "linear-gradient(135deg, #ff5252, #ff1744)"
                      : call.isAmbient || call.type === "Ambient"
                        ? "linear-gradient(135deg, #ffb74d, #fb8c00)"
                        : "linear-gradient(135deg, #2196F3, #1976D2)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  {call.isWebViewCall ? (
                    <CallOutlinedIcon sx={{ fontSize: 13, color: "white" }} />
                  ) : (
                    <MDTypography sx={{ fontSize: 13, textAlign: "center" }}>
                      {call.isSOS
                        ? "🚨"
                        : call.isAmbient || call.type === "Ambient"
                          ? "🎙️"
                          : call.isVideo
                            ? "🎥"
                            : "🔊"}
                    </MDTypography>
                  )}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <MDTypography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.6rem",
                      textAlign: "center",
                      lineHeight: 1.1,
                      color: call.isSOS
                        ? "#ff1744"
                        : call.isAmbient || call.type === "Ambient"
                          ? "#ef6c00"
                          : "#2196F3",
                      textTransform: "uppercase",
                      letterSpacing: "0.35px",
                    }}
                  >
                    {call.isSOS
                      ? "SOS Call"
                      : call.isAmbient || call.type === "Ambient"
                        ? "Ambient Call"
                        : `${
                            call.isVideo ? "Video" : "Audio"
                          } Call`}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      lineHeight: 1.1,
                      textAlign: "center",
                      color: "text",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      ml: 2.25,
                    }}
                  >
                    {call.callerDisplayWithNumber ||
                      call.callerDisplayName ||
                      (call.cidName && call.cidNumber
                        ? `${call.cidName} (${call.cidNumber})`
                        : call.cidName || call.cidNumber) ||
                      call.sender ||
                      call.streamId ||
                      "Unknown"}
                  </MDTypography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 0.95, flexShrink: 0 }}>
                <Tooltip title="Join">
                  <IconButton
                    size="small"
                    onClick={() => onJoin?.(call)}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0.35,
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.success.main, 0.32)}`,
                      bgcolor: (theme) =>
                        alpha(theme.palette.success.main, 0.12),
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.success.main, 0.22),
                      },
                    }}
                  >
                    {call.isVideo ? (
                      <VideocamOutlinedIcon
                        sx={{ fontSize: 14 }}
                        color="success"
                      />
                    ) : (
                      <CallOutlinedIcon sx={{ fontSize: 14 }} color="success" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    onClick={() => onReject?.(call)}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0.35,
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.error.main, 0.32)}`,
                      bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.error.main, 0.22),
                      },
                    }}
                  >
                    <CallEndOutlinedIcon sx={{ fontSize: 14 }} color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </MDBox>
      )}
    </MDBox>
  );
}
